import React, { createContext, useContext, useState, ReactNode } from 'react';

export type QueuedDeleteNotification = {
  id: string;
  subject: string;
  status: 'queued' | 'deleting' | 'deleted' | 'failed';
  error?: string;
};

type NotificationDeleteQueueContextType = {
  queuedDeleteNotifications: QueuedDeleteNotification[];
  addNotificationToDeleteQueue: (notification: { id: string; subject: string }) => boolean;
  removeNotificationFromDeleteQueue: (id: string) => void;
  clearDeleteQueue: () => void;
  clearCompletedDeletes: () => void;
  updateDeleteNotificationStatus: (id: string, status: QueuedDeleteNotification['status'], error?: string) => void;
  isDeleteQueueVisible: boolean;
  setDeleteQueueVisible: (visible: boolean) => void;
  isProcessingDelete: boolean;
  setIsProcessingDelete: (processing: boolean) => void;
};

const NotificationDeleteQueueContext = createContext<NotificationDeleteQueueContextType | undefined>(undefined);

export const NotificationDeleteQueueProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [queuedDeleteNotifications, setQueuedDeleteNotifications] = useState<QueuedDeleteNotification[]>([]);
  const [isDeleteQueueVisible, setDeleteQueueVisible] = useState(false);
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);

  const addNotificationToDeleteQueue = (notification: { id: string; subject: string }): boolean => {
    const exists = queuedDeleteNotifications.some(n => n.id === notification.id);
    if (exists) {
      return false;
    }
    setQueuedDeleteNotifications((prev) => [
      ...prev,
      { id: notification.id, subject: notification.subject, status: 'queued' },
    ]);
    setDeleteQueueVisible(true);
    return true;
  };

  const removeNotificationFromDeleteQueue = (id: string) => {
    setQueuedDeleteNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const clearDeleteQueue = () => {
    setQueuedDeleteNotifications([]);
  };

  const clearCompletedDeletes = () => {
    setQueuedDeleteNotifications((prev) => prev.filter((n) => n.status !== 'deleted' && n.status !== 'failed'));
  };

  const updateDeleteNotificationStatus = (id: string, status: QueuedDeleteNotification['status'], error?: string) => {
    setQueuedDeleteNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, status, error } : n))
    );
  };

  return (
    <NotificationDeleteQueueContext.Provider
      value={{
        queuedDeleteNotifications,
        addNotificationToDeleteQueue,
        removeNotificationFromDeleteQueue,
        clearDeleteQueue,
        clearCompletedDeletes,
        updateDeleteNotificationStatus,
        isDeleteQueueVisible,
        setDeleteQueueVisible,
        isProcessingDelete,
        setIsProcessingDelete,
      }}
    >
      {children}
    </NotificationDeleteQueueContext.Provider>
  );
};

export const useNotificationDeleteQueue = (): NotificationDeleteQueueContextType => {
  const context = useContext(NotificationDeleteQueueContext);
  if (!context) {
    throw new Error('useNotificationDeleteQueue must be used within a NotificationDeleteQueueProvider');
  }
  return context;
};
